import React from "react";
import TextArea from "devextreme-react/text-area";
import DataGrid, {
    Paging,
    FilterRow,
    Column,
    GroupPanel,
    Pager,
    SearchPanel,
    Scrolling,
    Selection,
    Export,
    Toolbar,
    Item,
} from "devextreme-react/data-grid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faUser,
    faCheck,
    faXmark,
    faClipboardList,
    faArrowUpRightFromSquare,
    faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import { faFaceThinking } from "@fortawesome/pro-regular-svg-icons";
import RightToWorkSessionReviewDetails from "./RightToWorkSessionReviewDetails";

interface IdVerificationSessionReviewPopupContentProps {
    idVerificationSessions: any[];
    selectedIdVerificationSessionId: number | string | undefined;
    selectedIdVerificationSession: any;
    selectedVerificationDecision: string;
    selectedVerificationReviewerNotes: string;
    selectedVerificationIsActive: boolean;
    onSessionSelectionChanged: (event: any) => void;
    onSessionExporting: (event: any) => void;
    onDecisionChange: (decision: string) => void;
    onReviewerNotesChange: (notes: string) => void;
    onActiveStatusChange: (isActive: boolean) => void;
    onApply: () => void;
    formatSessionReviewDateTime: (value: string) => string;
    getSessionReviewPillCssModifier: (status: string) => string;
    getSessionReviewHeaderStatus: (session: any) => string;
    getSessionReviewStateClass: (status: string) => string;
    getSessionReviewState: (session: any) => string;
    getVerificationPillCssModifier: (status: string) => string;
     
    /** Optional overrides so the same content can be reused (e.g. Right To Work sessions). */
    heading?: string;
    noDataText?: string;
    /** "rightToWork" renders the Right To Work summary and checks from the session's draftRightToWorkCheck. */
    variant?: "idVerification" | "rightToWork";
}

/**
 * Renders shared session-review popup content for ID verification.
 * The parent provides handlers and formatters so behavior remains unchanged.
 */
const IdVerificationSessionReviewPopupContent: React.FC<IdVerificationSessionReviewPopupContentProps> = ({
    idVerificationSessions,
    selectedIdVerificationSessionId,
    selectedIdVerificationSession,
    selectedVerificationDecision,
    selectedVerificationReviewerNotes,
    selectedVerificationIsActive,
    onSessionSelectionChanged,
    onSessionExporting,
    onDecisionChange,
    onReviewerNotesChange,
    onActiveStatusChange,
    onApply,
    formatSessionReviewDateTime,
    getSessionReviewPillCssModifier,
    getSessionReviewHeaderStatus,
    getSessionReviewStateClass,
    getSessionReviewState,
    getVerificationPillCssModifier,
    heading = "Id Verification Sessions",
    noDataText = "No ID verification sessions available",
    variant = "idVerification",
}) => {
    const isRightToWork = variant === "rightToWork";

    return (
        <div className="artist-profile__verification-popup-body">
            <h2 className="artist-profile__verification-popup-heading">{heading}</h2>
            <div className="artist-profile__verification-grid-wrap">
                <DataGrid
                    dataSource={idVerificationSessions}
                    keyExpr="draftVerificationSessionId"
                    selectedRowKeys={[selectedIdVerificationSessionId]}
                    showBorders={false}
                    showColumnLines={false}
                    hoverStateEnabled={true}
                    columnAutoWidth={true}
                    allowColumnReordering={true}
                    allowColumnResizing={true}
                    columnResizingMode={"widget"}
                    columnMinWidth={50}
                    onSelectionChanged={onSessionSelectionChanged}
                    onExporting={onSessionExporting}
                    noDataText={noDataText}
                    className="bank-grid artist-profile__verification-sessions-grid"
                >
                    <SearchPanel visible={true} placeholder={"Search"} />
                    <Export enabled={true} texts={{ exportAll: "Export all data to Excel" }} />
                    <Toolbar>
                        <Item name="searchPanel" locateInMenu="never" />
                        <Item name="exportButton" locateInMenu="never" />
                    </Toolbar>
                    <GroupPanel visible={true} />
                    <Scrolling useNative={true} showScrollbar={"always"} />
                    <Paging defaultPageSize={5} />
                    <Pager showPageSizeSelector={true} allowedPageSizes={[5, 10, 20]} showInfo={true} />
                    <Selection mode="single" />
                    <Column dataField="draftVerificationSessionId" caption="Id" />
                    <Column dataField="draftSessionId" caption="Session Id" />
                    <Column dataField="draftUserTrackingId" caption="User Tracking Id" />
                    <Column dataField="draftActiveLabel" caption="Active" />
                    <Column dataField="draftOverallStatusLabel" caption="Overall Status" />
                    <Column dataField="draftStatusLabel" caption="Status" />
                    <Column
                        dataField="draftBioConsentValue"
                        caption="BIO Consent"
                        calculateCellValue={(rowData: any) => formatSessionReviewDateTime(rowData.draftBioConsentValue)}
                    />
                    <Column dataField="draftLastUpdatedBy" caption="Last Updated By" />
                    <Column
                        dataField="draftLastUpdatedOn"
                        caption="Last Updated On"
                        calculateCellValue={(rowData: any) => formatSessionReviewDateTime(rowData.draftLastUpdatedOn)}
                    />
                    <FilterRow visible={true} applyFilter="auto" />
                </DataGrid>
            </div>

            {isRightToWork && selectedIdVerificationSession?.draftRightToWorkCheck ? (
                <RightToWorkSessionReviewDetails
                    check={selectedIdVerificationSession.draftRightToWorkCheck}
                    formatSessionReviewDateTime={formatSessionReviewDateTime}
                />
            ) : null}

            {selectedIdVerificationSession && !isRightToWork ? (
                <div className="artist-profile__verification-session-review">
                    <div className="artist-profile__verification-session-review-header">
                        <div>
                            <div className="artist-profile__verification-session-review-kicker">IDENTITY VERIFICATION</div>
                            <h3 className="artist-profile__verification-session-review-title">Session review</h3>
                        </div>
                        <span
                            className={`artist-profile__verification-session-review-pill ${getSessionReviewPillCssModifier(
                                selectedIdVerificationSession?.draftOverallStatusLabel || "",
                            )}`}
                        >
                            <FontAwesomeIcon icon={faTriangleExclamation} aria-hidden="true" />
                            <span>{getSessionReviewHeaderStatus(selectedIdVerificationSession)}</span>
                        </span>
                    </div>

                    <div className="row artist-profile__verification-session-review-row">
                        <div className="col-12 col-lg-3">
                            <div className="artist-profile__verification-session-review-card">
                                <div className="artist-profile__verification-session-review-label">Session ID</div>
                                <div className="artist-profile__verification-session-review-value artist-profile__verification-session-review-value--truncate">
                                    {selectedIdVerificationSession.draftSessionId || "-"}
                                </div>
                            </div>
                        </div>
                        <div className="col-12 col-lg-3">
                            <div className="artist-profile__verification-session-review-card">
                                <div className="artist-profile__verification-session-review-label">User tracking ID</div>
                                <div className="artist-profile__verification-session-review-value">
                                    {selectedIdVerificationSession.draftUserTrackingId || "-"}
                                </div>
                            </div>
                        </div>
                        <div className="col-12 col-lg-3">
                            <div className="artist-profile__verification-session-review-card">
                                <div className="artist-profile__verification-session-review-label">Biometric consent</div>
                                <div className="artist-profile__verification-session-review-value artist-profile__verification-session-review-value--meta">
                                    {formatSessionReviewDateTime(selectedIdVerificationSession.draftBioConsentValue)}
                                </div>
                            </div>
                        </div>
                        <div className="col-12 col-lg-3">
                            <div className="artist-profile__verification-session-review-card">
                                <div className="artist-profile__verification-session-review-label">Session state</div>
                                <div
                                    className={`artist-profile__verification-session-review-value ${getSessionReviewStateClass(
                                        getSessionReviewState(selectedIdVerificationSession),
                                    )}`}
                                >
                                    {getSessionReviewState(selectedIdVerificationSession)}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ) : null}

            {selectedIdVerificationSession ? (
                <>
                    {isRightToWork ? null : (
                        <>
                            <div className="artist-profile__verification-subtitle">VERIFICATION CHECKS</div>
                            {selectedIdVerificationSession.draftVerificationSections.map((section: any) => (
                                <div className="artist-profile__verification-card" key={section.draftSectionFieldName}>
                                    <div className="artist-profile__verification-card-header">
                                        <div className="artist-profile__verification-card-title">
                                            <span className="artist-profile__verification-card-icon-wrap">
                                                <FontAwesomeIcon
                                                    icon={
                                                        section.draftSectionFieldName === "draftLiveness"
                                                            ? faFaceThinking
                                                            : section.draftSectionFieldName === "draftFaceMatch"
                                                              ? faUser
                                                              : faClipboardList
                                                    }
                                                    className="artist-profile__verification-card-icon"
                                                    aria-hidden="true"
                                                />
                                            </span>
                                            <span>{section.draftSectionLabel}</span>
                                        </div>
                                        <span
                                            className={`artist-profile__verification-pill ${getVerificationPillCssModifier(
                                                section.draftSectionRecommendation || "",
                                            )}`}
                                        >
                                            {section.draftSectionRecommendation ||
                                                (section.draftSectionApproved ? "Approve" : "Requires Review")}
                                        </span>
                                    </div>

                                    <div className="row artist-profile__verification-checks-row">
                                        {section.draftChecks.map((check: any) => (
                                            <div className="col-12 col-lg-4" key={check.draftCheckFieldName}>
                                                {check.draftCheckFieldName === "draftAiFaceMatchConfidence" ? (
                                                    <div className="artist-profile__verification-check-item artist-profile__verification-check-item--confidence">
                                                        <span className="artist-profile__verification-check-item-main">
                                                            <span className="artist-profile__verification-check-icon artist-profile__verification-check-icon--pass">
                                                                <FontAwesomeIcon icon={faCheck} aria-hidden="true" />
                                                            </span>
                                                            <span>AI face match</span>
                                                        </span>
                                                        <span className="artist-profile__verification-confidence-text">
                                                            Confidence {Math.round(check.draftConfidenceScore * 100)}%
                                                        </span>
                                                        <span className="artist-profile__verification-confidence-bar">
                                                            <span
                                                                className="artist-profile__verification-confidence-bar-fill"
                                                                style={{ width: `${Math.round(check.draftConfidenceScore * 100)}%` }}
                                                            ></span>
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <div className="artist-profile__verification-check-item">
                                                        <span
                                                            className={`artist-profile__verification-check-icon ${
                                                                check.draftCheckPassed
                                                                    ? "artist-profile__verification-check-icon--pass"
                                                                    : "artist-profile__verification-check-icon--review"
                                                            }`}
                                                        >
                                                            <FontAwesomeIcon
                                                                icon={check.draftCheckPassed ? faCheck : faXmark}
                                                                aria-hidden="true"
                                                            />
                                                        </span>
                                                        <span>
                                                            {check.draftCheckLabel} ({check.draftCheckResultLabel || (check.draftCheckPassed ? "Pass" : "Fail")})
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </>
                    )}

                    <div className="artist-profile__verification-subtitle artist-profile__verification-subtitle--decision">
                        REVIEWER DECISION
                    </div>
                    <div className="artist-profile__verification-decision-panel">
                        <div className="row">
                            <div className="col-12 col-lg-4 mb-3">
                                <button
                                    type="button"
                                    className={`btn btn--ghost btn--ghost--teal btn--large mt-3 artist-profile__verification-decision-button artist-profile__verification-decision-button--approve ${
                                        selectedVerificationDecision === "Approve"
                                            ? "artist-profile__verification-decision-button--active"
                                            : ""
                                    }`}
                                    onClick={() => onDecisionChange("Approve")}
                                >
                                    <FontAwesomeIcon icon={faCheck} aria-hidden="true" />
                                    <span className="ml-2">Approve</span>
                                </button>
                            </div>
                            <div className="col-12 col-lg-4 mb-3">
                                <button
                                    type="button"
                                    className={`btn btn--ghost btn--ghost--teal btn--large mt-3 artist-profile__verification-decision-button artist-profile__verification-decision-button--reject ${
                                        selectedVerificationDecision === "Reject"
                                            ? "artist-profile__verification-decision-button--active"
                                            : ""
                                    }`}
                                    onClick={() => onDecisionChange("Reject")}
                                >
                                    <FontAwesomeIcon icon={faXmark} aria-hidden="true" />
                                    <span className="ml-2">Reject</span>
                                </button>
                            </div>
                            <div className="col-12 col-lg-4 mb-3">
                                <button
                                    type="button"
                                    className={`btn btn--ghost btn--ghost--teal btn--large mt-3 artist-profile__verification-decision-button artist-profile__verification-decision-button--requires-review ${
                                        selectedVerificationDecision === "Requires Review"
                                            ? "artist-profile__verification-decision-button--active"
                                            : ""
                                    }`}
                                    onClick={() => onDecisionChange("Requires Review")}
                                >
                                    <span className="artist-profile__verification-decision-arrow">
                                        <FontAwesomeIcon icon={faArrowUpRightFromSquare} aria-hidden="true" />
                                    </span>
                                    <span>Requires Review</span>
                                </button>
                            </div>
                        </div>

                        <TextArea
                            height={140}
                            width={"100%"}
                            readOnly={false}
                            value={selectedVerificationReviewerNotes}
                            valueChangeEvent="keyup"
                            onValueChanged={(event: any) => onReviewerNotesChange(event.value)}
                            placeholder={"Reviewer notes (required)..."}
                        />

                        <div className="artist-profile__verification-footer">
                            <div className="artist-profile__verification-reviewer-text">
                                {selectedIdVerificationSession.draftReviewedBy} · {formatSessionReviewDateTime(selectedIdVerificationSession.draftReviewedOn)}
                            </div>
                            <div className="artist-profile__verification-footer-actions">
                                <fieldset className="artist-profile__verification-active-toggle" aria-label="Active status">
                                    <legend className="artist-profile__verification-active-toggle-label">Active</legend>
                                    <div className="artist-profile__verification-active-toggle-options">
                                        <label className="artist-profile__verification-active-toggle-option">
                                            <input
                                                type="radio"
                                                name="verification-active-status"
                                                checked={selectedVerificationIsActive}
                                                onChange={() => onActiveStatusChange(true)}
                                            />
                                            <span
                                                className="artist-profile__verification-active-toggle-control"
                                                aria-hidden="true"
                                            ></span>
                                            <span>Yes</span>
                                        </label>
                                        <label className="artist-profile__verification-active-toggle-option">
                                            <input
                                                type="radio"
                                                name="verification-active-status"
                                                checked={!selectedVerificationIsActive}
                                                onChange={() => onActiveStatusChange(false)}
                                            />
                                            <span
                                                className="artist-profile__verification-active-toggle-control"
                                                aria-hidden="true"
                                            ></span>
                                            <span>No</span>
                                        </label>
                                    </div>
                                </fieldset>
                                <button
                                    type="button"
                                    className="btn btn-primary btn--large artist-profile__verification-apply-button"
                                    onClick={onApply}
                                >
                                    APPLY
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            ) : null}
        </div>
    );
};

export default IdVerificationSessionReviewPopupContent;
